/**
 * Copy helpers for the empty-match waitlist on provider results.
 */

/** Business id on a lead that has no matched provider yet. */
export const EMPTY_MATCH_BUSINESS_ID = 'empty-match';

export function emptyMatchHeadline() {
  return 'No exact provider matches found';
}

function servicePhrase(tradeLabel) {
  const trade = typeof tradeLabel === 'string' ? tradeLabel.trim() : '';
  if (!trade) return 'local';
  if (trade.toUpperCase() === 'HVAC') return 'HVAC';
  return trade.toLowerCase();
}

/**
 * Intro under the headline. Mentions the other-trade list only when that list is on the page.
 * @param {string} tradeLabel
 * @param {boolean} hasOtherProviders
 */
export function emptyMatchSubcopy(tradeLabel, hasOtherProviders) {
  const matchLine = 'Fill out the form and our team will work to match you with a provider';
  if (!hasOtherProviders) return `${matchLine}.`;
  return `${matchLine}, or explore other ${servicePhrase(tradeLabel)} providers below.`;
}

/**
 * Confirmation after Notify me. Mentions the other-trade list only when that list is on the page.
 * @param {string} preferredContactMethod
 * @param {string} [tradeLabel]
 * @param {boolean} [hasOtherProviders]
 */
export function waitlistSuccessMessage(
  preferredContactMethod,
  tradeLabel,
  hasOtherProviders,
) {
  let message;
  if (preferredContactMethod === 'phone') {
    message = "Got it — we'll call when a pro is ready.";
  } else if (preferredContactMethod === 'text') {
    message = "Got it — we'll text when a pro is ready.";
  } else {
    message = "Got it — we'll email when a pro is ready.";
  }
  if (!hasOtherProviders) return message;
  return `${message} Browse other ${servicePhrase(tradeLabel)} providers below.`;
}

/**
 * Copy when returning to results after the waitlist was already submitted for this search.
 * @param {string} [tradeLabel]
 * @param {boolean} [hasOtherProviders]
 */
export function waitlistAlreadySubmittedMessage(tradeLabel, hasOtherProviders) {
  const message =
    "You're already on the list — our team is working to find you a provider.";
  if (!hasOtherProviders) return message;
  return `${message} Browse other ${servicePhrase(tradeLabel)} providers below.`;
}

const WAITLIST_SUBMITTED_SESSION_KEY = 'nky_connect_waitlist_submitted_ids';

function readWaitlistSubmittedIds() {
  try {
    const raw = sessionStorage.getItem(WAITLIST_SUBMITTED_SESSION_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

/** Remember a waitlist signup for this browser tab session (survives back navigation). */
export function markWaitlistSubmitted(searchRequestId) {
  const id = String(searchRequestId || '').trim();
  if (!id) return;
  try {
    const ids = readWaitlistSubmittedIds();
    if (ids.includes(id)) return;
    sessionStorage.setItem(
      WAITLIST_SUBMITTED_SESSION_KEY,
      JSON.stringify([...ids, id]),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

export function hasWaitlistSubmitted(searchRequestId) {
  const id = String(searchRequestId || '').trim();
  if (!id) return false;
  return readWaitlistSubmittedIds().includes(id);
}
